'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type { ChangeEvent } from 'react';
import { useRef, useState } from 'react';
import { AnonymityDisclosureModal } from '@/components/gift/anonymity-disclosure';
import { AnimatedSwitch } from '@/components/ui/animated-switch';
import { Label } from '@/components/ui/label';
import { TextareaField } from '@/components/ui/textarea-field';
import { GIFT_MESSAGE_MAX_BYTES } from '@/config/gift';
import { useGiftFormStore } from '@/stores/gift-form';
function utf8Bytes(text: string): number {
  return new TextEncoder().encode(text).length;
}

/**
 * Step 3 of the gift flow — add an optional message and choose anonymity.
 *
 * This step is fully skippable; the orchestrator surfaces a "Skip" button so
 * users who don't want to personalize jump straight to review. Validation is
 * only the byte-length check on the message — no required fields.
 */
export function SendGiftStep3() {
  const $ = _c(51);
  const t = useTranslations("gift.send");
  const message = useGiftFormStore(_temp);
  const anonymous = useGiftFormStore(_temp2);
  const setMessage = useGiftFormStore(_temp3);
  const setAnonymous = useGiftFormStore(_temp4);
  const [disclosureOpen, setDisclosureOpen] = useState(false);
  const hasSeenDisclosureRef = useRef(false);
  const messageBytes = utf8Bytes(message);
  const messageTooLong = messageBytes > GIFT_MESSAGE_MAX_BYTES;
  let t0;
  if ($[0] !== setAnonymous) {
    t0 = function handleAnonymousToggle(next) {
      if (next && !hasSeenDisclosureRef.current) {
        setDisclosureOpen(true);
        return;
      }
      setAnonymous(next);
    };
    $[0] = setAnonymous;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const handleAnonymousToggle = t0;
  let t1;
  if ($[2] !== t) {
    t1 = t("message.label");
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t) {
    t2 = t("message.placeholder");
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== setMessage) {
    t3 = e => setMessage(e.target.value);
    $[6] = setMessage;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== message || $[9] !== messageTooLong || $[10] !== t1 || $[11] !== t2 || $[12] !== t3) {
    t4 = <TextareaField id="gift-message" label={t1} placeholder={t2} rows={3} value={message} onChange={t3} aria-invalid={messageTooLong} maxLength={GIFT_MESSAGE_MAX_BYTES * 2} />;
    $[8] = message;
    $[9] = messageTooLong;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const t5 = messageTooLong ? "text-caption text-feedback-error-content" : "text-caption text-foreground-secondary";
  const t6 = Math.max(0, GIFT_MESSAGE_MAX_BYTES - messageBytes);
  let t7;
  if ($[14] !== t || $[15] !== t6) {
    t7 = t("message.remaining", {
      count: t6
    });
    $[14] = t;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t5 || $[18] !== t7) {
    t8 = <p className={t5}>{t7}</p>;
    $[17] = t5;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t4 || $[21] !== t8) {
    t9 = <div className="flex flex-col gap-2">{t4}{t8}</div>;
    $[20] = t4;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t) {
    t10 = t("anonymous.label");
    $[23] = t;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10) {
    t11 = <Label htmlFor="gift-anonymous">{t10}</Label>;
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t) {
    t12 = t("anonymous.description");
    $[27] = t;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t12) {
    t13 = <p className="text-caption text-foreground-secondary">{t12}</p>;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t11 || $[32] !== t13) {
    t14 = <div className="flex flex-col gap-1">{t11}{t13}</div>;
    $[31] = t11;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== anonymous || $[35] !== handleAnonymousToggle) {
    t15 = <AnimatedSwitch id="gift-anonymous" checked={anonymous} onCheckedChange={handleAnonymousToggle} />;
    $[34] = anonymous;
    $[35] = handleAnonymousToggle;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t14 || $[38] !== t15) {
    t16 = <div className="flex items-start justify-between gap-3">{t14}{t15}</div>;
    $[37] = t14;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== t16 || $[41] !== t9) {
    t17 = <div className="flex flex-col gap-4">{t9}{t16}</div>;
    $[40] = t16;
    $[41] = t9;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== setAnonymous) {
    t18 = () => {
      hasSeenDisclosureRef.current = true;
      setAnonymous(true);
    };
    $[43] = setAnonymous;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== disclosureOpen || $[46] !== t18) {
    t19 = <AnonymityDisclosureModal open={disclosureOpen} onOpenChange={setDisclosureOpen} onConfirm={t18} />;
    $[45] = disclosureOpen;
    $[46] = t18;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== t17 || $[49] !== t19) {
    t20 = <>{t17}{t19}</>;
    $[48] = t17;
    $[49] = t19;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  return t20;
}
function _temp4(s_2) {
  return s_2.setAnonymous;
}
function _temp3(s_1) {
  return s_1.setMessage;
}
function _temp2(s_0) {
  return s_0.anonymous;
}
function _temp(s) {
  return s.message;
}